import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Timezone-aware date helpers. Extending the dayjs singleton here is idempotent and, because dayjs
// is a shared instance, also enables .utc()/.tz() for any module that imports dayjs after this one.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The platform default org timezone. MUST match the backend's DEFAULT_TZ in
 * helpers/cloudKey/datetime.js (getOrgTimezone) — if the two ever disagree, a scheduled unlock's
 * fire instant computed on the web will differ from the passage window the server derives, i.e. the
 * door fires at the wrong time. All current customers are AU/Brisbane (no DST).
 */
export const DEFAULT_ORG_TIMEZONE = 'Australia/Brisbane';

/** True if `tz` is an IANA zone that this runtime's ICU recognises (same test the backend uses). */
export const isValidTimezone = (tz) => {
	if (!tz || typeof tz !== 'string') return false;
	try {
		new Intl.DateTimeFormat('en-AU', { timeZone: tz });
		return true;
	} catch {
		return false;
	}
};

/**
 * Resolve the org's IANA timezone from its loaded settings — the exact mirror of the backend's
 * getOrgTimezone(orgID): the ORG_TIMEZONE setting if it's a valid IANA name, else DEFAULT_ORG_TIMEZONE.
 * @param {Array<{defaultSettingID:number,value:string}>} orgSettings
 * @param {number} settingID  the ORG_TIMEZONE default-setting id (CONSTANTS_SETTINGS.ORG_TIMEZONE)
 */
export const resolveTimezoneFromSettings = (orgSettings, settingID) => {
	const tz = (orgSettings || []).find((s) => s.defaultSettingID === settingID)?.value?.trim();
	return isValidTimezone(tz) ? tz : DEFAULT_ORG_TIMEZONE;
};

/**
 * Reinterpret picked wall-clock fields AS being in `tz` and return an offset-carrying ISO-8601
 * string. This is the safety-critical step: the admin picks "9:00 AM"; we anchor that to the lock's
 * timezone (not the browser's) so the fire instant is correct regardless of where the admin is.
 * @param {string} wallClock  naive local fields, 'YYYY-MM-DDTHH:mm:ss' (NO offset)
 * @param {string} tz         IANA zone to interpret them in
 * @returns {string} e.g. '2026-07-22T09:00:00+10:00'
 */
export const wallClockToZonedIso = (wallClock, tz) => resolveWallClock(wallClock, tz).iso;

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

/** Find the instant a zone's offset changes, to the minute, between two straddling bounds. */
const findTransition = (loMs, hiMs, tz) => {
	const offsetAt = (ms) => dayjs(ms).tz(tz).utcOffset();
	const startOffset = offsetAt(loMs);
	let lo = loMs;
	let hi = hiMs;
	while (hi - lo > MINUTE_MS) {
		const mid = lo + Math.floor((hi - lo) / 2);
		if (offsetAt(mid) === startOffset) lo = mid;
		else hi = mid;
	}
	return hi;
};

/**
 * Resolve picked wall-clock fields against `tz`, reporting the two daylight-saving boundary cases
 * that `dayjs.tz()` silently guesses through. Verified against dayjs 1.11: picking 02:30 on
 * 2026-10-04 in Australia/Sydney (a time that does not exist) yields 03:30+11:00 with no signal —
 * the admin asks for 2:30 AM and the door opens at 3:30 AM. Picking 02:30 on 2026-04-05 (a time
 * that happens twice) silently resolves to the SECOND occurrence.
 *
 * Total by design — never throws, so display paths stay safe. Callers on the scheduling write path
 * must check `status` and refuse to submit a `nonexistent` time rather than shift it.
 *
 * @returns {{iso: string|null, status: 'ok'|'nonexistent'|'ambiguous'|'invalid',
 *   offsetMinutes: number|null, skippedFrom?: string, skippedTo?: string, alternatives?: string[]}}
 *   `ambiguous` resolves to the EARLIER instant: for an access-control event, opening at the first
 *   valid moment is safer than opening an hour later than the operator expected.
 */
export const resolveWallClock = (wallClock, tz) => {
	const invalid = { iso: null, status: 'invalid', offsetMinutes: null };
	if (!wallClock || !isValidTimezone(tz)) return invalid;

	const asUtcMs = dayjs.utc(wallClock).valueOf();
	if (Number.isNaN(asUtcMs)) return invalid;

	const offsetAt = (ms) => dayjs(ms).tz(tz).utcOffset();
	const isoAt = (ms) => dayjs(ms).tz(tz).format();

	// Probe the zone's offset a day either side, then test which candidate instants round-trip back
	// to the requested wall clock. A gap satisfies neither; an overlap satisfies both.
	const offsetBefore = offsetAt(asUtcMs - DAY_MS);
	const offsetAfter = offsetAt(asUtcMs + DAY_MS);
	const candidateBefore = asUtcMs - offsetBefore * MINUTE_MS;
	const candidateAfter = asUtcMs - offsetAfter * MINUTE_MS;
	const beforeHolds = offsetAt(candidateBefore) === offsetBefore;
	const afterHolds = offsetAt(candidateAfter) === offsetAfter;

	if (!beforeHolds && !afterHolds) {
		// Spring-forward gap: this local time never occurs. Report the skipped range so the caller can
		// tell the admin exactly which times are unavailable instead of silently moving the unlock.
		const transition = findTransition(asUtcMs - DAY_MS, asUtcMs + DAY_MS, tz);
		const gapMinutes = offsetAfter - offsetBefore;
		const resumesAt = dayjs(transition).tz(tz);
		return {
			iso: null,
			status: 'nonexistent',
			offsetMinutes: null,
			skippedFrom: resumesAt.subtract(gapMinutes, 'minute').format('h:mm A'),
			skippedTo: resumesAt.format('h:mm A')
		};
	}

	if (beforeHolds && afterHolds && candidateBefore !== candidateAfter) {
		// Fall-back overlap: this local time occurs twice. Take the earlier instant, deliberately.
		const earlier = Math.min(candidateBefore, candidateAfter);
		const later = Math.max(candidateBefore, candidateAfter);
		return {
			iso: isoAt(earlier),
			status: 'ambiguous',
			offsetMinutes: offsetAt(earlier),
			alternatives: [isoAt(earlier), isoAt(later)]
		};
	}

	const instant = beforeHolds ? candidateBefore : candidateAfter;
	return { iso: isoAt(instant), status: 'ok', offsetMinutes: offsetAt(instant) };
};

/** Format a UTC ISO instant in `tz`. Returns '' for a null/invalid input. */
export const formatInZone = (iso, tz, fmt = 'D MMM, h:mm A') => {
	if (!iso) return '';
	const d = dayjs.utc(iso);
	if (!d.isValid()) return '';
	return d.tz(tz).format(fmt);
};

/** `value` (or now) as a dayjs in `tz`; the viewer's zone when `tz` is null. */
export const dayjsInZone = (value, tz) => (tz ? dayjs(value).tz(tz) : dayjs(value));

/** Short zone abbreviation for `tz` at `instant` (DST-correct), e.g. 'AEST'. '' if unresolved. */
export const zoneAbbrev = (tz, instant = new Date()) => {
	try {
		return (
			new Intl.DateTimeFormat('en-AU', { timeZone: tz, timeZoneName: 'short' })
				.formatToParts(instant)
				.find((p) => p.type === 'timeZoneName')?.value || ''
		);
	} catch {
		return '';
	}
};

/** The viewer's own IANA timezone (for surfacing a browser-vs-org-zone mismatch to the admin). */
export const browserTimezone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
	} catch {
		return '';
	}
};
